import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Settings as SettingsIcon, User, Bell, Lock, Instagram, Youtube, Music, MessageSquare } from 'lucide-react';
import { useSocialMediaStore } from '../../stores/socialMediaStore';
import { useAuth } from '../../contexts/AuthContext';
import ProfileSettings from './settings/ProfileSettings';
import NotificationSettings from './settings/NotificationSettings';
import SecuritySettings from './settings/SecuritySettings';
import SocialAccounts from './settings/SocialAccounts';
import SupportPreferences from './settings/SupportPreferences';

interface SettingsProps {
  user: {
    name: string;
    isNewUser: boolean;
  };
}

export default function Settings({ user }: SettingsProps) {
  const [activeSection, setActiveSection] = useState('profile');
  const { accounts, connectAccount, disconnectAccount } = useSocialMediaStore();
  const { updateProfile, updatePassword } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Check for tab parameter in URL
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'redes-sociais') {
      setActiveSection('social');
    }
  }, [location]);

  const renderSettingsContent = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileSettings user={user} onUpdate={updateProfile} />;
      case 'notifications':
        return <NotificationSettings />;
      case 'security':
        return <SecuritySettings onPasswordChange={updatePassword} />;
      case 'social':
        return (
          <SocialAccounts
            accounts={accounts}
            onConnect={connectAccount}
            onDisconnect={disconnectAccount}
          />
        );
      case 'support':
        return <SupportPreferences />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
            <SettingsIcon className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configurações</h1>
            <p className="text-gray-600">Gerencie as configurações de sua conta</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Settings Navigation */}
          <div className="space-y-2">
            <button
              onClick={() => setActiveSection('profile')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeSection === 'profile'
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <User className="h-5 w-5" />
              <span>Perfil</span>
            </button>

            <button
              onClick={() => setActiveSection('notifications')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeSection === 'notifications'
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Bell className="h-5 w-5" />
              <span>Notificações</span>
            </button>

            <button
              onClick={() => setActiveSection('security')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeSection === 'security'
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Lock className="h-5 w-5" />
              <span>Segurança</span>
            </button>

            <button
              onClick={() => setActiveSection('social')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeSection === 'social'
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Instagram className="h-5 w-5" />
              <span>Redes Sociais</span>
            </button>
            
            <button
              onClick={() => setActiveSection('support')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeSection === 'support'
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <MessageSquare className="h-5 w-5" />
              <span>Suporte</span>
            </button>
          </div>

          {/* Settings Content */}
          <div className="lg:col-span-3">
            {renderSettingsContent()}
          </div>
        </div>
      </div>
    </div>
  );
}